export const LANGUAGE_STORAGE_KEY = 'lang';
export const DEFAULT_LANGUAGE = 'zh-CN';

const normalizeRawLanguage = (language) =>
  typeof language === 'string' ? language.trim().replace(/_/g, '-') : '';

const normalizeStorageLanguage = (language) => {
  const normalized = normalizeRawLanguage(language);
  return normalized.length > 0 ? normalized : null;
};

const normalizeSupportedLanguageList = (supportedLanguages) => {
  if (!Array.isArray(supportedLanguages)) {
    return [];
  }

  const normalizedLanguages = [];
  const seenLanguages = new Set();

  supportedLanguages.forEach((item) => {
    const normalizedLanguage = normalizeRawLanguage(item);
    if (!normalizedLanguage) {
      return;
    }

    const dedupeKey = normalizedLanguage.toLowerCase();
    if (seenLanguages.has(dedupeKey)) {
      return;
    }

    seenLanguages.add(dedupeKey);
    normalizedLanguages.push(normalizedLanguage);
  });

  return normalizedLanguages;
};

export const normalizeLanguageCode = (language, supportedLanguages = []) => {
  const normalizedLanguage = normalizeRawLanguage(language);
  if (!normalizedLanguage) {
    return null;
  }

  const normalizedSupportedLanguages = normalizeSupportedLanguageList(supportedLanguages);
  if (normalizedSupportedLanguages.length === 0) {
    return normalizedLanguage;
  }

  const lowerCasedInput = normalizedLanguage.toLowerCase();
  const exactMatch = normalizedSupportedLanguages.find(
    (supportedLanguage) => supportedLanguage.toLowerCase() === lowerCasedInput
  );
  if (exactMatch) {
    return exactMatch;
  }

  const baseLanguage = lowerCasedInput.split('-')[0];
  if (!baseLanguage) {
    return null;
  }

  const preferredByBase = normalizedSupportedLanguages.find((supportedLanguage) => {
    const supportedBaseLanguage = supportedLanguage.toLowerCase().split('-')[0];
    return supportedBaseLanguage === baseLanguage;
  });

  return preferredByBase || null;
};

export const readLanguageFromStorage = (storageKey = LANGUAGE_STORAGE_KEY) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return normalizeStorageLanguage(window.localStorage.getItem(storageKey));
  } catch (error) {
    return null;
  }
};

export const writeLanguageToStorage = (language, storageKey = LANGUAGE_STORAGE_KEY) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const normalizedLanguage = normalizeStorageLanguage(language);

  try {
    if (!normalizedLanguage) {
      window.localStorage.removeItem(storageKey);
      return true;
    }

    window.localStorage.setItem(storageKey, normalizedLanguage);
    return true;
  } catch (error) {
    return false;
  }
};

const normalizeLanguageQuality = (candidateTokens) => {
  if (!Array.isArray(candidateTokens) || candidateTokens.length <= 1) {
    return 1;
  }

  const qualityTokenMatch = candidateTokens
    .slice(1)
    .map((token) => token.trim())
    .map((token) => token.match(/^q\s*=\s*(.+)$/i))
    .find(Boolean);

  if (!qualityTokenMatch) {
    return 1;
  }

  const qualityValue = Number.parseFloat(
    qualityTokenMatch[1].trim().replace(/^(["'])(.*)\1$/, '$2')
  );
  if (!Number.isFinite(qualityValue)) {
    return 1;
  }

  return Math.min(1, Math.max(0, qualityValue));
};

const normalizeBrowserLanguageCandidate = (candidate) => {
  if (typeof candidate !== 'string') {
    return null;
  }

  const candidateTokens = candidate.split(';');
  const normalizedCandidate = normalizeRawLanguage(candidateTokens[0] || '');

  if (!normalizedCandidate || normalizedCandidate === '*') {
    return null;
  }

  const quality = normalizeLanguageQuality(candidateTokens);
  if (quality <= 0) {
    return null;
  }

  return {
    language: normalizedCandidate,
    quality
  };
};

const extractBrowserLanguageCandidates = (candidate) => {
  if (typeof candidate !== 'string') {
    return [];
  }

  return candidate
    .split(',')
    .map((token, index) => ({
      index,
      parsedCandidate: normalizeBrowserLanguageCandidate(token)
    }))
    .filter(({ parsedCandidate }) => parsedCandidate)
    .sort((left, right) => {
      if (left.parsedCandidate.quality !== right.parsedCandidate.quality) {
        return right.parsedCandidate.quality - left.parsedCandidate.quality;
      }

      return left.index - right.index;
    })
    .map(({ parsedCandidate }) => parsedCandidate.language);
};

const detectBrowserLanguageCandidates = () => {
  if (typeof navigator === 'undefined') {
    return [];
  }

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
    navigator.userLanguage,
    navigator.browserLanguage
  ];

  const normalizedCandidates = [];
  const seenCandidates = new Set();

  candidates.forEach((candidate) => {
    const expandedCandidates = extractBrowserLanguageCandidates(candidate);
    expandedCandidates.forEach((normalizedCandidate) => {
      const dedupeKey = normalizedCandidate.toLowerCase();
      if (seenCandidates.has(dedupeKey)) {
        return;
      }

      seenCandidates.add(dedupeKey);
      normalizedCandidates.push(normalizedCandidate);
    });
  });

  return normalizedCandidates;
};

export const detectBrowserLanguage = () => {
  const browserLanguageCandidates = detectBrowserLanguageCandidates();
  return browserLanguageCandidates[0] || null;
};

export const resolvePreferredLanguage = ({
  defaultLanguage,
  supportedLanguages = [],
  storageKey = LANGUAGE_STORAGE_KEY
} = {}) => {
  const normalizedSupportedLanguages = normalizeSupportedLanguageList(supportedLanguages);
  const normalizedDefaultLanguage = normalizeLanguageCode(
    defaultLanguage,
    normalizedSupportedLanguages
  );
  const normalizedFallbackLanguage = normalizeLanguageCode(
    DEFAULT_LANGUAGE,
    normalizedSupportedLanguages
  );

  const fallbackLanguage =
    normalizedDefaultLanguage
    || normalizedFallbackLanguage
    || normalizedSupportedLanguages[0]
    || DEFAULT_LANGUAGE;

  const storedLanguage = normalizeLanguageCode(
    readLanguageFromStorage(storageKey),
    normalizedSupportedLanguages
  );
  if (storedLanguage) {
    return storedLanguage;
  }

  const browserLanguageCandidates = detectBrowserLanguageCandidates();
  for (const browserLanguageCandidate of browserLanguageCandidates) {
    const browserLanguage = normalizeLanguageCode(
      browserLanguageCandidate,
      normalizedSupportedLanguages
    );
    if (browserLanguage) {
      return browserLanguage;
    }
  }

  return fallbackLanguage;
};
